/**
 * Entity change helpers shared by every command handler:
 * undoable add/delete/modify + entity lookup + prompt plumbing.
 * All CAD mutations flow through the store API (A2 contract) so the scene
 * ledger stays mirrored.
 */

import { useAppStore } from '../../state/store';
import type { Entity } from '../../state/store';
import { useCommandInteract } from './commandInteract';
import { useLayoutStore } from '../../state/useLayoutStore';
import { useBlockStore } from '../../state/commandAuxStores';
import { useConstraintStore } from '../constraints/constraintStore';
import { constraintIdsBoundTo } from '../constraints/constraintOps';
import type { ConstraintPayload } from '../constraints/types';

/** Rough snapshot footprint for the undo budget (Faz 10.3): JSON length of
 *  the captured payload — a ceiling estimate only, accuracy not required. */
function estimateSnapshotBytes(value: unknown): number {
    try {
        return JSON.stringify(value ?? null)?.length ?? 0;
    } catch {
        // Non-serializable payload (circulars, view refs): fall back to the
        // store's default per-entry estimate.
        return 0;
    }
}

/** Add entities as ONE undoable step; returns their ids. */
export function undoableAdd(entities: Entity[], label?: string): string[] {
    const app = useAppStore.getState();
    entities.forEach((e) => app.addEntity(e));
    const ids = entities.map((e) => e.id);
    app.pushHistory({
        type: 'cmd.add',
        timestamp: Date.now(),
        data: { count: ids.length },
        label,
        estimatedBytes: estimateSnapshotBytes(entities),
        undo: () => { const a = useAppStore.getState(); ids.forEach((id) => a.deleteEntity(id)); },
        redo: () => { const a = useAppStore.getState(); entities.forEach((e) => a.addEntity(e)); },
    });
    return ids;
}

/** Delete entity ids as ONE undoable step. Constraints referencing the
 *  deleted entities (or their bound dimensions) die inside the SAME step,
 *  so undo restores entity + constraint set together (Faz 6.4, OCS rule:
 *  entity deletion removes its constraints in one undo transaction). */
export function undoableDelete(ids: string[], label?: string): void {
    const app = useAppStore.getState();
    const snapshots = ids.map((id) => app.entities[id]).filter(Boolean) as Entity[];
    const constraintBefore: ConstraintPayload | null = useConstraintStore.getState().set.constraints.length > 0
        ? useConstraintStore.getState().serializePayload()
        : null;
    const doomedConstraints = constraintIdsBoundTo(ids);
    if (doomedConstraints.length > 0) {
        useConstraintStore.getState().removeConstraints(doomedConstraints);
    }
    const constraintAfter: ConstraintPayload | null = doomedConstraints.length > 0
        ? useConstraintStore.getState().serializePayload()
        : null;
    ids.forEach((id) => app.deleteEntity(id));
    app.pushHistory({
        type: 'cmd.delete',
        timestamp: Date.now(),
        data: { count: snapshots.length },
        label,
        estimatedBytes: estimateSnapshotBytes(snapshots) + estimateSnapshotBytes(constraintBefore) + estimateSnapshotBytes(constraintAfter),
        undo: () => {
            const a = useAppStore.getState();
            snapshots.forEach((e) => a.addEntity(e));
            if (constraintBefore) useConstraintStore.getState().applyPayload(constraintBefore);
        },
        redo: () => {
            const a = useAppStore.getState();
            snapshots.forEach((e) => a.deleteEntity(e.id));
            if (constraintAfter) useConstraintStore.getState().applyPayload(constraintAfter);
        },
    });
}

/** Modify data of entity ids as ONE undoable step. The optional label names
 *  the step in the QAT undo history dropdown (OCS history_dropdown_labels). */
export function undoableModify(ids: string[], mutate: (data: Record<string, any>, entity: Entity) => Record<string, any>, label?: string): void {
    const app = useAppStore.getState();
    const before = new Map<string, Record<string, any>>();
    const after = new Map<string, Record<string, any>>();
    ids.forEach((id) => {
        const e = app.entities[id];
        if (!e) return;
        before.set(id, structuredClone(e.data));
        const next = mutate(structuredClone(e.data), e);
        after.set(id, next);
        useAppStore.getState().updateEntity(id, { data: next });
    });
    app.pushHistory({
        type: 'cmd.modify',
        timestamp: Date.now(),
        data: { count: ids.length },
        label,
        estimatedBytes: estimateSnapshotBytes([...before.values()]) + estimateSnapshotBytes([...after.values()]),
        undo: () => { const a = useAppStore.getState(); before.forEach((d, id) => a.updateEntity(id, { data: d })); },
        redo: () => { const a = useAppStore.getState(); after.forEach((d, id) => a.updateEntity(id, { data: d })); },
    });
}

/** Apply an arbitrary per-entity patch (data bag AND/OR top-level fields
 *  like layer/name) to entity ids as ONE undoable step. The Properties
 *  panel's bulk edits land here so a 500-selection change is one U. */
export function undoablePatch(ids: string[], patch: (entity: Entity) => Partial<Entity> | null, label?: string): void {
    const app = useAppStore.getState();
    const before = new Map<string, Entity>();
    const after = new Map<string, Partial<Entity>>();
    ids.forEach((id) => {
        const e = app.entities[id];
        if (!e) return;
        before.set(id, structuredClone(e));
        const updates = patch(structuredClone(e));
        if (!updates) return;
        after.set(id, updates);
        useAppStore.getState().updateEntity(id, updates);
    });
    if (after.size === 0) return;
    app.pushHistory({
        type: 'cmd.patch',
        timestamp: Date.now(),
        data: { count: after.size },
        label,
        estimatedBytes: estimateSnapshotBytes([...before.values()]) + estimateSnapshotBytes([...after.values()]),
        undo: () => { const a = useAppStore.getState(); before.forEach((e, id) => a.updateEntity(id, e)); },
        redo: () => { const a = useAppStore.getState(); after.forEach((u, id) => a.updateEntity(id, structuredClone(u))); },
    });
}

/** Make a CAD drafting entity. In an active paper-space layout the entity
 *  is tagged so it draws on the sheet (Faz 9) instead of in model space;
 *  in an open Block Editor session it is tagged with the block scope so
 *  the working set stays isolated (Faz 5.15). */
export function makeEntity(type: string, name: string, data: Record<string, any>, layer?: string): Entity {
    const app = useAppStore.getState();
    const layoutId = app.mode === '2d' ? useLayoutStore.getState().activeId : null;
    const tagged = layoutId && data.onSheet === undefined
        ? { ...data, onSheet: layoutId }
        : data;
    const editingBlock = useBlockStore.getState().editingBlock;
    const scoped = editingBlock && tagged.blockScope === undefined
        ? { ...tagged, blockScope: editingBlock.name }
        : tagged;
    return {
        id: `cad:${crypto.randomUUID()}`,
        type,
        name: name || type,
        visible: true,
        locked: false,
        layer: layer ?? app.activeLayerId,
        data: scoped,
    };
}

/** Delete `oldIds` and add `newEntities` as ONE undoable step. */
export function undoableReplace(oldIds: string[], newEntities: Entity[], label?: string): string[] {
    const app = useAppStore.getState();
    const snapshots = oldIds.map((id) => app.entities[id]).filter(Boolean) as Entity[];
    oldIds.forEach((id) => app.deleteEntity(id));
    newEntities.forEach((e) => app.addEntity(e));
    const newIds = newEntities.map((e) => e.id);
    app.pushHistory({
        type: 'cmd.replace',
        timestamp: Date.now(),
        data: { count: newIds.length },
        label,
        estimatedBytes: estimateSnapshotBytes(snapshots) + estimateSnapshotBytes(newEntities),
        undo: () => {
            const a = useAppStore.getState();
            newIds.forEach((id) => a.deleteEntity(id));
            snapshots.forEach((e) => a.addEntity(e));
        },
        redo: () => {
            const a = useAppStore.getState();
            snapshots.forEach((e) => a.deleteEntity(e.id));
            newEntities.forEach((e) => a.addEntity(e));
        },
    });
    return newIds;
}

/** Polyline entity from points. */
export function polylineEntity(points: [number, number, number][], opts: { name?: string; closed?: boolean; color?: string; layer?: string; type?: string } = {}) {
    return makeEntity(opts.type ?? 'polyline', opts.name ?? `Polyline-${Date.now()}`, {
        vertices: points.map((p) => [p[0], p[1], p[2] ?? 0]),
        closed: !!opts.closed,
        color: opts.color ?? '#ffffff',
    }, opts.layer);
}

/** Line entity. */
export function lineEntity(a: [number, number, number], b: [number, number, number], color = '#ffffff') {
    return makeEntity('line', `Line-${Date.now()}`, { start: a, end: b, color });
}

/** Start an interaction spec (the mounted CommandInteractScope collects points). */
export function startInteraction(spec: import('./commandInteract').InteractionSpec): void {
    useCommandInteract.getState().start(spec);
}
